import React, { useState, useEffect } from "react";
import {
  doc,
  updateDoc,
  getDoc,
  getDocs,
  collection,
  setDoc,
  query,
  where,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../../utils/firebase";
import {
  X,
  Shield,
  Users,
  Bell,
  Save,
  Palette,
  Layers,
  InfoIcon,
  Home,
  Building2,
  Plus,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Check,
  Settings as SettingsIcon,
  Filter,
  Search,
  Edit,
  User,
  UserPlus,
  Key,
  Trash2,
  RefreshCw,
  Mail,
  DollarSign,
  Calendar,
  HelpCircle,
} from "lucide-react";
import { useAuth } from "../../hooks/AuthenticationProvider";

// Add this component to your SettingsModal.jsx file

const NotificationSettingsTab = () => {
  const { user, organizationId } = useAuth();
  const [notificationPrefs, setNotificationPrefs] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Define notification types based on your system
  const NOTIFICATION_TYPES = [
    {
      id: "manual",
      label: "Manual Messages",
      description: "Messages sent directly from staff",
    },
    {
      id: "residencyMilestone",
      label: "Residency Milestones",
      description: "Notifications about residency duration milestones",
    },
    {
      id: "residentNotes",
      label: "Resident Notes",
      description: "Notifications about notes added to resident profiles",
    },
    {
      id: "sobrietyBirthday",
      label: "Sobriety Birthdays",
      description: "Notifications about sobriety anniversaries",
    },
    {
      id: "birthday",
      label: "Birthdays",
      description: "Notifications about resident birthdays",
    },
    {
      id: "profileUpdate",
      label: "Profile Updates",
      description: "Notifications when resident profiles are updated",
    },
    // Add other notification types as needed
  ];

  useEffect(() => {
    const fetchNotificationPreferences = async () => {
      if (!user?.email || !organizationId) return;

      try {
        // Fixed path: use user.email instead of user.id
        const prefsRef = doc(
          db,
          `organizations/${organizationId}/users/${user.email}`
        );
        const prefsSnap = await getDoc(prefsRef);

        if (prefsSnap.exists()) {
          const data = prefsSnap.data();
          // If notificationPreferences exists in the user document, use it
          if (data.notificationPreferences) {
            setNotificationPrefs(data.notificationPreferences);
          } else {
            // Default settings - opt in to all in-app notifications by default, but not emails
            const defaultPrefs = NOTIFICATION_TYPES.reduce((acc, type) => {
              acc[type.id] = { inApp: true, email: false };
              return acc;
            }, {});

            setNotificationPrefs(defaultPrefs);
          }
        } else {
          // Default settings if user document doesn't exist
          const defaultPrefs = NOTIFICATION_TYPES.reduce((acc, type) => {
            acc[type.id] = { inApp: true, email: false };
            return acc;
          }, {});

          setNotificationPrefs(defaultPrefs);
        }
      } catch (error) {
        console.error("Error fetching notification preferences:", error);
      }
    };

    fetchNotificationPreferences();
  }, [user, organizationId]);

  const handleTogglePreference = (typeId, channel) => {
    setNotificationPrefs((prev) => ({
      ...prev,
      [typeId]: {
        ...prev[typeId],
        [channel]: !(prev[typeId]?.[channel] ?? false),
      },
    }));
  };

  const savePreferences = async () => {
    if (!user?.email || !organizationId) {
      console.error("Missing user email or organization ID");
      return;
    }

    setIsSaving(true);
    try {
      // Fixed path: update directly to the user document
      const userRef = doc(
        db,
        `organizations/${organizationId}/users/${user.email}`
      );

      // Update the user document with notification preferences
      await updateDoc(userRef, {
        notificationPreferences: notificationPrefs,
        notificationPreferencesUpdatedAt: new Date(),
      });

      console.log("Saved notification preferences successfully");
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error("Error saving notification preferences:", error);
      alert("Failed to save preferences. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
        <h3 className="text-lg font-medium text-white">
          Notification Preferences
        </h3>
        <button
          onClick={savePreferences}
          disabled={isSaving}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2 w-full sm:w-auto"
        >
          <Save size={18} />
          {isSaving ? "Saving..." : "Save Preferences"}
        </button>
      </div>

      {saveSuccess && (
        <div className="bg-green-500/20 text-green-400 p-3 rounded-lg">
          Settings saved successfully!
        </div>
      )}

      <div className="bg-slate-800/50 rounded-lg overflow-hidden">
        <div className="grid grid-cols-3 px-4 sm:px-6 py-3 bg-slate-700/50 border-b border-slate-600">
          <div className="col-span-1 font-medium text-slate-300 text-sm sm:text-base">
            Notification Type
          </div>
          <div className="text-center font-medium text-slate-300 text-sm sm:text-base">
            In-App
          </div>
          <div className="text-center font-medium text-slate-300 text-sm sm:text-base">
            Email
          </div>
        </div>

        <div className="divide-y divide-slate-700/30">
          {NOTIFICATION_TYPES.map((type) => (
            <div
              key={type.id}
              className="grid grid-cols-3 px-4 sm:px-6 py-4 items-center hover:bg-slate-700/20 transition-colors"
            >
              <div className="col-span-1">
                <p className="font-medium text-slate-200 text-sm sm:text-base">
                  {type.label}
                </p>
                <p className="text-xs sm:text-sm text-slate-400 hidden sm:block">
                  {type.description}
                </p>
              </div>
              <div className="flex justify-center">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationPrefs[type.id]?.inApp ?? true}
                    onChange={() => handleTogglePreference(type.id, "inApp")}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 sm:w-11 sm:h-6 bg-slate-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 sm:after:h-5 sm:after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                </label>
              </div>
              <div className="flex justify-center">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationPrefs[type.id]?.email ?? false}
                    onChange={() => handleTogglePreference(type.id, "email")}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 sm:w-11 sm:h-6 bg-slate-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 sm:after:h-5 sm:after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="p-4 bg-slate-800/30 border border-slate-700/50 rounded-lg">
        <p className="text-xs sm:text-sm text-yellow-400">
          <strong>Note:</strong> Email notifications will be sent based on your
          preferences above. You must have a valid email address in your profile
          to receive emails. If you disable in-app notifications for a certain
          type, you will still receive emails if that option is enabled.
        </p>
      </div>
    </div>
  );
};

const SettingsModal = ({ onClose, initialTab }) => {
  const {
    user,
    organizationId,
    role,
    houseSettings,
    organizationData,
    checkPermission,
    isLoading,
    defaultPermissions: originalPermissions,
  } = useAuth();

  const [houses, setHouses] = useState([]);
  const [users, setUsers] = useState([]);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserRole, setNewUserRole] = useState("Staff");
  const [activeTab, setActiveTab] = useState(initialTab || "appearance");
  const [permissions, setPermissions] = useState({});
  const [newUserFirstName, setNewUserFirstName] = useState("");
  const [newUserLastName, setNewUserLastName] = useState("");
  const [expandedSection, setExpandedSection] = useState("residents");
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [selectedColors, setSelectedColors] = useState({});
  const [expandedHouse, setExpandedHouse] = useState(null);
  const [isAddingHouse, setIsAddingHouse] = useState(false);
  const [moveInFee, setMoveInFee] = useState(0);
  const [changesSaved, setChangesSaved] = useState(false);
  const [residentDashboardCount, setResidentDashboardCount] = useState(0);
  const [headerLogo, setHeaderLogo] = useState(
    organizationData?.houseSettings?.headerLogo || null
  );
  const [headerTitle, setHeaderTitle] = useState(
    organizationData?.houseSettings?.headerTitle || ""
  );
  const [headerContact, setHeaderContact] = useState(
    organizationData?.houseSettings?.headerContact || ""
  );
  const [toast, setToast] = useState(null);
  const [features, setFeatures] = useState({
    // Staff Features
    recoveryCapital: true,
    payments: true,
    records: true,
    uaRecording: true,
    uaScheduling: true,
    analytics: true,
    phases: true,
    sponsorship: true,
    customForms: true,
    applications: true,
    bedPlatform: true,
    ownerDashboard: true,
    schedule: true, // Added staff schedule feature

    // Resident Dashboard Features
    overnightRequest: true,
    residentPayments: true,
    residentPhases: true,
    residentRecoveryCapital: true,
    residentUAs: true,
    geoLocationTracking: true,
    residentSchedule: true,
  });

  const [newHouse, setNewHouse] = useState({
    name: "",
    fee: 0,
    color: "",
    floors: [
      {
        id: 1,
        rooms: [
          {
            id: 1,
            name: "Room 1",
            beds: [{ id: "1A", resident: null }],
          },
        ],
      },
    ],
  });

  const [needsBedUpdate, setNeedsBedUpdate] = useState(
    organizationData?.currentTotalBeds > organizationData?.paidBedCount
  );

  const availableRoles = ["Staff", "Admin", "Leader", "Resident"];
  const permissionManagementRoles = ["Admin", "Staff", "Leader"];

  const getDefaultColor = (index) => {
    // Array of professional, distinct colors for houses
    const defaultColors = [
      "#FF5A5F", // coral red
      "#00A699", // teal
      "#FC642D", // orange
      "#4F3CC9", // purple
      "#28A745", // green
      "#3B7EA1", // blue
      "#F7B731", // yellow
      "#7952B3", // violet
      "#E9406A", // pink
      "#00BCD4", // cyan
      "#C0392B", // dark red
      "#16A085", // emerald green
      "#F39C12", // tangerine
      "#8E44AD", // amethyst
      "#2ECC71", // light green
      "#3498DB", // bright blue
      "#E67E22", // carrot orange
      "#D35400", // pumpkin
      "#1ABC9C", // turquoise
      "#9B59B6", // wisteria
    ];

    // Return color based on index, cycling through the array if more houses than colors
    return defaultColors[index % defaultColors.length];
  };

  const fetchResidentDashboardCount = async () => {
    try {
      const houseSettingsRef = doc(
        db,
        `organizations/${organizationId}/houseSettings/initial`
      );
      const snapshot = await getDoc(houseSettingsRef);
      if (snapshot.exists()) {
        setResidentDashboardCount(
          snapshot.data().paidResDashboardCount ||
            snapshot.data().residentDashboardCount ||
            10
        );

        // Also fetch the moveInFee when getting dashboard count
        setMoveInFee(snapshot.data().moveInFee || 0);
      }
    } catch (error) {
      console.error("Error fetching dashboard count:", error);
    }
  };

  // Fetch users for the organization
  const fetchUsers = async (orgId) => {
    const usersQuery = collection(db, "organizations", orgId, "users");
    const usersSnapshot = await getDocs(usersQuery);
    return usersSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  };

  // Combine fetching data based on organizationId
  // Fetch dashboard count and users
  useEffect(() => {
    if (!organizationId) return;

    const fetchData = async () => {
      try {
        // Fetch dashboard count
        await fetchResidentDashboardCount();

        // Fetch users
        const fetchedUsers = await fetchUsers(organizationId);
        setUsers(fetchedUsers);
      } catch (error) {
        console.error("Error fetching organization data:", error);
      }
    };

    fetchData();
  }, [organizationId]);

  // Handle permissions and defaults
  useEffect(() => {
    if (
      !organizationData?.houseSettings?.permissions ||
      !originalPermissions ||
      !organizationId
    )
      return;

    let hasChanges = false; // Track if changes were made

    const updatedPermissions = {
      ...organizationData.houseSettings.permissions,
    };

    for (const [role, perms] of Object.entries(originalPermissions)) {
      if (!updatedPermissions[role]) {
        updatedPermissions[role] = { ...perms };
        hasChanges = true; // Mark as modified
      } else {
        for (const [key, value] of Object.entries(perms)) {
          if (updatedPermissions[role][key] === undefined) {
            updatedPermissions[role][key] = value;
            hasChanges = true; // Mark as modified
          }
        }
      }
    }

    setPermissions(updatedPermissions);

    // Write changes back to Firestore if any
    if (hasChanges) {
      const houseSettingsRef = doc(
        db,
        "organizations",
        organizationId,
        "houseSettings",
        "initial"
      );

      updateDoc(houseSettingsRef, { permissions: updatedPermissions })
        .then(() => console.log("Permissions updated in Firestore"))
        .catch((error) => console.error("Error updating permissions:", error));
    }
  }, [organizationData, originalPermissions, organizationId]);

  // Handle house settings and colors
  useEffect(() => {
    if (!organizationData) return;

    // Your existing code for houses
    const housesArray = organizationData.housesData
      ? Object.entries(organizationData.housesData).map(([name, data]) => ({
          ...data,
          name,
        }))
      : [];

    console.log("Transformed Houses Array:", housesArray);
    setHouses(housesArray);

    const colors = {};
    housesArray.forEach((house) => {
      if (house.name && house.color) {
        colors[house.name] = house.color;
      }
    });

    setSelectedColors(colors);

    // Load the header information if available
    if (organizationData.houseSettings) {
      setHeaderLogo(organizationData.houseSettings.headerLogo || null);
      setHeaderTitle(organizationData.houseSettings.headerTitle || "");
      setHeaderContact(organizationData.houseSettings.headerContact || "");
    }
  }, [organizationData]);

  useEffect(() => {
    const loadFeatures = async () => {
      if (!organizationId) return;

      try {
        const houseSettingsRef = doc(
          db,
          `organizations/${organizationId}/houseSettings/initial`
        );
        const settingsDoc = await getDoc(houseSettingsRef);

        if (settingsDoc.exists()) {
          // Get existing features from Firestore
          let existingFeatures = settingsDoc.data().features || {};

          // Handle legacy format (flat structure) if it exists
          if (!existingFeatures.staff && !existingFeatures.resident) {
            existingFeatures = {
              staff: {
                recoveryCapital: existingFeatures.recoveryCapital || true,
                payments: existingFeatures.payments || true,
                records: existingFeatures.records || true,
                uaRecording: existingFeatures.uaRecording || true,
                uaScheduling: existingFeatures.uaScheduling || true,
                analytics: existingFeatures.analytics || true,
                phases: existingFeatures.phases || true,
                sponsorship: existingFeatures.sponsorship || true,
                customForms: existingFeatures.customForms || true,
                applications: existingFeatures.applications || true,
                bedPlatform: existingFeatures.bedPlatform || true,
                ownerDashboard: existingFeatures.ownerDashboard || true,
                schedule: true, // Default to true for new feature
              },
              resident: {
                overnightRequest: existingFeatures.overnightRequest || true,
                payments: existingFeatures.payments || true,
                phases: existingFeatures.phases || true,
                recoveryCapital: existingFeatures.recoveryCapital || true,
                ua:
                  (existingFeatures.uaRecording || true) &&
                  (existingFeatures.uaScheduling || true),
                geoLocation: true, // New feature
                schedule: true, // New feature
              },
            };
          }

          setFeatures(existingFeatures);
        }
      } catch (error) {
        console.error("Error loading features:", error);
      }
    };

    loadFeatures();
  }, [organizationId]);

  const handleFeatureToggle = (feature, category) => {
    setFeatures((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [feature]: !prev[category][feature],
      },
    }));
  };

  const handleSaveFeatures = async () => {
    if (!organizationId) return;

    try {
      const houseSettingsRef = doc(
        db,
        `organizations/${organizationId}/houseSettings/initial`
      );
      await updateDoc(houseSettingsRef, {
        features,
        featuresLastUpdated: new Date(),
        featuresUpdatedBy: user.email,
      });

      setToast({
        message: "Feature settings saved successfully!",
        type: "success",
      });
    } catch (error) {
      console.error("Error saving features:", error);
      alert("Error saving feature settings. Please try again.");
    }
  };

  const getFeatureLabel = (feature) => {
    const labels = {
      // Staff labels
      recoveryCapital: "Recovery Capital",
      payments: "Payments & Invoicing",
      uaRecording: "UA Recording",
      uaScheduling: "UA Scheduling",
      analytics: "Analytics",
      phases: "Phase System",
      sponsorship: "Sponsorship",
      customForms: "Custom Forms",
      applications: "Applications",
      bedPlatform: "Bed Platform",
      records: "Records",
      ownerDashboard: "Owner Dashboard",
      schedule: "Schedule",

      // Resident labels
      overnightRequest: "Overnight Requests",
      ua: "UA Dashboard",
      geoLocation: "Geo-Location Tracking",
    };

    return labels[feature] || feature;
  };

  const handleMoveInFeeChange = (value) => {
    setMoveInFee(parseFloat(value) || 0);
  };

  const handleColorChange = async (houseName, color) => {
    setSelectedColors((prev) => ({
      ...prev,
      [houseName]: color,
    }));

    // Find the house index
    const updatedHouses = houses.map((house) =>
      house.name === houseName ? { ...house, color } : house
    );

    setHouses(updatedHouses);

    try {
      const houseSettingsRef = doc(
        db,
        `organizations/${organizationId}/houseSettings/initial`
      );
      await updateDoc(houseSettingsRef, { houses: updatedHouses });

      console.log(`Updated color for ${houseName} to ${color}`);
    } catch (error) {
      console.error("Error updating house color:", error);
      alert("Error updating house color. Please try again.");
    }
  };

  // Add this utility function at the top of your SettingsModal component
  const normalizeEmail = (email) => {
    return email?.toLowerCase().trim() || "";
  };

  const capitalizeFirstLetter = (str) => {
    return str?.charAt(0).toUpperCase() + str?.slice(1).toLowerCase() || "";
  };

  // Updated handleAddUser function
  const handleAddUser = async (e) => {
    e.preventDefault();

    if (!newUserEmail || !newUserFirstName || !newUserLastName) {
      alert("Please fill out all fields before adding a user.");
      return;
    }

    // Normalize email and capitalize names
    const normalizedEmail = normalizeEmail(newUserEmail);
    const capitalizedFirstName = capitalizeFirstLetter(newUserFirstName);
    const capitalizedLastName = capitalizeFirstLetter(newUserLastName);

    // Check for duplicate users using normalized email
    const isDuplicateUser = users.some(
      (user) => normalizeEmail(user.email) === normalizedEmail
    );

    if (isDuplicateUser) {
      alert("User with this email already exists.");
      return;
    }

    // Check resident dashboard limit
    if (newUserRole === "Resident") {
      const currentResidentCount = users.filter(
        (user) => user.role === "Resident"
      ).length;
      const maxResidents = residentDashboardCount || 10;
      if (currentResidentCount >= maxResidents) {
        alert(
          `Cannot add more resident users. Your limit is ${maxResidents} residents.`
        );
        return;
      }
    }

    try {
      // Use normalized email as the document ID
      const userRef = doc(
        db,
        "organizations",
        organizationId,
        "users",
        normalizedEmail
      );

      await setDoc(userRef, {
        email: normalizedEmail, // Store normalized email
        firstName: capitalizedFirstName, // Store capitalized first name
        lastName: capitalizedLastName, // Store capitalized last name
        role: newUserRole,
        addedBy: user.email,
        createdAt: new Date(),
      });

      // Update local state with normalized/capitalized data
      setUsers((prevUsers) => [
        ...prevUsers,
        {
          email: normalizedEmail,
          firstName: capitalizedFirstName,
          lastName: capitalizedLastName,
          role: newUserRole,
        },
      ]);

      // Clear form fields
      setNewUserEmail("");
      setNewUserFirstName("");
      setNewUserLastName("");
      setNewUserRole("Staff");

      setToast({
        message: `User ${capitalizedFirstName} ${capitalizedLastName} added successfully!`,
        type: "success",
      });
    } catch (error) {
      console.error("Error adding user:", error);
      setToast({
        message: "Error adding user. Please try again.",
        type: "error",
      });
    }
  };

  const handleSaveHouseChanges = async () => {
    if (!organizationId) return;

    try {
      const formattedHouses = houses.map((house, houseIndex) => ({
        name: house.name.trim(),
        fee: house.fee || 0,
        color: house.color || getDefaultColor(houseIndex),
        floors: house.floors.map((floor, floorIndex) => ({
          id: floorIndex + 1,
          rooms: floor.rooms.map((room, roomIndex) => ({
            id: roomIndex + 1,
            name: room.name || `Room ${roomIndex + 1}`,
            beds: room.beds.map((bed, bedIndex) => ({
              id: `${floorIndex + 1}${String.fromCharCode(65 + roomIndex)}${
                bedIndex + 1
              }`,
              resident: bed.resident || null,
            })),
          })),
        })),
      }));

      const houseSettingsRef = doc(
        db,
        `organizations/${organizationId}/houseSettings/initial`
      );

      // Include moveInFee in the update
      await updateDoc(houseSettingsRef, {
        houses: formattedHouses,
        moveInFee: moveInFee,
      });

      await updateDoc(houseSettingsRef, { houses: formattedHouses });

      alert("House configurations updated successfully!");
    } catch (error) {
      console.error("Error updating houses:", error);
      alert("Error updating houses. Please try again.");
    }
  };

  const handleDeleteUser = async (email) => {
    // Ask for confirmation before deleting
    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${email} from the database?`
    );
    if (!confirmDelete) return;

    if (!organizationId) return;

    try {
      // Normalize the email for consistent lookup
      const normalizedEmail = normalizeEmail(email);

      // Use normalized email to find and delete the document
      const userDocRef = doc(
        db,
        "organizations",
        organizationId,
        "users",
        normalizedEmail
      );

      await deleteDoc(userDocRef);

      // Update local state - remove user with matching normalized email
      setUsers((prevUsers) =>
        prevUsers.filter(
          (user) => normalizeEmail(user.email) !== normalizedEmail
        )
      );

      setToast({
        message: `${email} has been deleted successfully.`,
        type: "success",
      });
    } catch (error) {
      console.error("Error removing user:", error);
      setToast({
        message: "Error removing user. Please try again.",
        type: "error",
      });
    }
  };
  const calculateTotalBeds = (housesArray) => {
    return housesArray.reduce((total, house) => {
      return (
        total +
        house.floors.reduce((floorTotal, floor) => {
          return (
            floorTotal +
            floor.rooms.reduce((roomTotal, room) => {
              return roomTotal + room.beds.length;
            }, 0)
          );
        }, 0)
      );
    }, 0);
  };

  // Handle saving houses
  const handleSaveHouses = async () => {
    if (!organizationId) return;

    for (const house of houses) {
      if (!house.name.trim()) {
        alert("All houses must have a valid name.");
        return;
      }
    }

    const formattedHouses = houses.map((house, index) => ({
      name: house.name.trim(),
      fee: house.fee || 0,
      color: house.color || getDefaultColor(index),
      floors: house.floors.map((floor, floorIndex) => ({
        id: floorIndex + 1,
        rooms: floor.rooms.map((room, roomIndex) => ({
          id: roomIndex + 1,
          name: room.name || `Room ${roomIndex + 1}`,
          beds: room.beds.map((bed, bedIndex) => ({
            id: `${floorIndex + 1}${String.fromCharCode(65 + roomIndex)}${
              bedIndex + 1
            }`,
            resident: null,
          })),
        })),
      })),
    }));

    try {
      const houseSettingsRef = doc(
        db,
        `organizations/${organizationId}/houseSettings/initial`
      );

      // Include headerLogo, headerTitle, and headerContact along with other settings
      await updateDoc(houseSettingsRef, {
        houses: formattedHouses,
        moveInFee: moveInFee,
        headerLogo: headerLogo,
        headerTitle: headerTitle,
        headerContact: headerContact,
      });

      setToast({
        message: "Settings saved successfully!",
        type: "success",
      });
    } catch (error) {
      console.error("Error updating houses:", error);
      alert("Error updating houses. Please try again.");
    }
  };

  const handlePermissionsChange = (role, permission, value) => {
    setPermissions((prev) => ({
      ...prev,
      [role]: {
        ...prev[role],
        [permission]: value,
      },
    }));
  };

  const handleSavePermissions = async () => {
    if (!organizationId) return;

    try {
      const houseSettingsDocRef = doc(
        db,
        "organizations",
        organizationId,
        "houseSettings",
        "initial"
      );

      await updateDoc(houseSettingsDocRef, {
        permissions,
        lastUpdated: new Date(),
        updatedBy: user.email,
      });

      alert("Permissions updated successfully!");
    } catch (error) {
      console.error("Error updating permissions:", error);
      alert("Error updating permissions. Please try again.");
    }
  };

  if (role !== "Owner") return null;

  const tabs = [
    {
      id: "appearance",
      label: "Appearance",
      icon: <Palette className="w-4 h-4 sm:w-5 sm:h-5" />,
    },
    {
      id: "users",
      label: "Users",
      icon: <Users className="w-4 h-4 sm:w-5 sm:h-5" />,
    },
    {
      id: "permissions",
      label: "Permissions",
      icon: <Shield className="w-4 h-4 sm:w-5 sm:h-5" />,
    },
    {
      id: "notifications",
      label: "Notifications",
      icon: <Bell className="w-4 h-4 sm:w-5 sm:h-5" />,
    },
    ...(role === "Owner"
      ? [
          {
            id: "features",
            label: "Features",
            icon: <Layers className="w-4 h-4 sm:w-5 sm:h-5" />,
          },
        ]
      : []),
    {
      id: "houses",
      label: "Houses",
      icon: <Home className="w-4 h-4 sm:w-5 sm:h-5" />,
    },
  ];

  const handleHouseChange = (index, field, value) => {
    const updatedHouses = [...houses];
    updatedHouses[index] = {
      ...updatedHouses[index],
      [field]: value,
    };
    setHouses(updatedHouses);
  };

  const handleFloorChange = (houseIndex, numberOfFloors) => {
    const updatedHouses = [...houses];
    const house = updatedHouses[houseIndex];

    const newFloors = Array(numberOfFloors)
      .fill(null)
      .map((_, floorIndex) => ({
        id: floorIndex + 1,
        rooms: house.floors[floorIndex]?.rooms || [
          {
            id: 1,
            name: `Room 1`,
            beds: [{ id: `${floorIndex + 1}A`, resident: null }],
          },
        ],
      }));

    updatedHouses[houseIndex] = {
      ...house,
      floors: newFloors,
    };
    setHouses(updatedHouses);
  };

  const handleRoomChange = (houseIndex, floorIndex, numberOfRooms) => {
    const updatedHouses = [...houses];
    const house = updatedHouses[houseIndex];

    const newFloors = house.floors.map((floor, fi) => {
      if (fi !== floorIndex) return floor;

      const newRooms = Array(numberOfRooms)
        .fill(null)
        .map((_, roomIndex) => ({
          id: roomIndex + 1,
          name: `Room ${roomIndex + 1}`,
          beds: floor.rooms[roomIndex]?.beds || [
            {
              id: `${floor.id}${String.fromCharCode(65 + roomIndex)}`,
              resident: null,
            },
          ],
        }));

      return {
        ...floor,
        rooms: newRooms,
      };
    });

    updatedHouses[houseIndex] = {
      ...house,
      floors: newFloors,
    };
    setHouses(updatedHouses);
  };

  const handleBedChange = (houseIndex, floorIndex, roomIndex, numberOfBeds) => {
    const updatedHouses = [...houses];
    const house = updatedHouses[houseIndex];

    // Get current number of beds in this room
    const currentBeds = house.floors[floorIndex].rooms[roomIndex].beds.length;

    // Only check limit when increasing beds
    if (numberOfBeds > currentBeds) {
      const bedDifference = numberOfBeds - currentBeds;
      const currentTotalBeds = calculateTotalBeds(houses);
      const paidBedCount = organizationData?.houseSettings?.paidBedCount || 0;

      if (currentTotalBeds + bedDifference > paidBedCount) {
        alert(`Cannot add more beds. Your paid limit is ${paidBedCount} beds.`);
        return;
      }
    }

    const newFloors = house.floors.map((floor, fi) => {
      if (fi !== floorIndex) return floor;

      const newRooms = floor.rooms.map((room, ri) => {
        if (ri !== roomIndex) return room;

        const newBeds = Array(numberOfBeds)
          .fill(null)
          .map((_, bedIndex) => ({
            id: `${floor.id}${String.fromCharCode(65 + roomIndex)}${
              bedIndex + 1
            }`,
            resident: null,
          }));

        return {
          ...room,
          beds: newBeds,
        };
      });

      return {
        ...floor,
        rooms: newRooms,
      };
    });

    updatedHouses[houseIndex] = {
      ...house,
      floors: newFloors,
    };
    setHouses(updatedHouses);
  };

  const handleRoomNameChange = (houseIndex, floorIndex, roomIndex, newName) => {
    const updatedHouses = [...houses];
    const house = updatedHouses[houseIndex];

    const newFloors = house.floors.map((floor, fi) => {
      if (fi !== floorIndex) return floor;

      const newRooms = floor.rooms.map((room, ri) => {
        if (ri !== roomIndex) return room;
        return {
          ...room,
          name: newName,
        };
      });

      return {
        ...floor,
        rooms: newRooms,
      };
    });

    updatedHouses[houseIndex] = {
      ...house,
      floors: newFloors,
    };
    setHouses(updatedHouses);
  };

  if (isLoading) {
    return <Spinner />;
  }

  // Toast notification component
  const Toast = ({ message, type = "success", onClose }) => {
    useEffect(() => {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }, [onClose]);

    const bgColor = type === "success" ? "bg-green-500/20" : "bg-red-500/20";
    const textColor = type === "success" ? "text-green-400" : "text-red-400";
    const icon =
      type === "success" ? <Check size={18} /> : <AlertCircle size={18} />;

    return (
      <div
        className={`fixed top-4 right-4 ${bgColor} ${textColor} p-4 rounded-lg shadow-lg z-50 flex items-center space-x-2 max-w-sm animate-fadeIn`}
      >
        {icon}
        <span>{message}</span>
        <button
          onClick={onClose}
          className="ml-2 text-gray-400 hover:text-gray-300"
        >
          <X size={16} />
        </button>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[9999]"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" />

      {/* Modal Container */}
      <div className="relative h-screen w-screen grid place-items-center p-1 sm:p-4">
        <div className="relative w-full max-w-3xl h-[90vh] sm:h-[80vh] bg-slate-900 rounded-xl shadow-2xl border border-slate-800 flex flex-col">
          {/* Header - remains fixed */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-slate-800">
            <h2 className="text-xl sm:text-2xl font-semibold text-slate-100">
              Settings
            </h2>
            {(!needsBedUpdate || changesSaved) && (
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-slate-300 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          <div className="flex flex-col flex-1 min-h-0">
            {/* Tabs - horizontal scrolling on mobile */}
            <div className="flex overflow-x-auto border-b border-slate-800 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center
                    px-1.5 sm:px-5
                    py-1.5 sm:py-3
                    space-x-1 sm:space-x-2
                    text-xs sm:text-sm
                  
        font-medium
        transition-colors
        whitespace-nowrap
        ${
          activeTab === tab.id
            ? "text-blue-400 border-b-2 border-blue-400 bg-slate-800/50"
            : "text-slate-400 hover:text-slate-300 hover:bg-slate-800/30"
        }
      `}
                >
                  {tab.icon}
                  <span className="text-xs xs:text-xs">{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Tab Content with Scroll */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-6 relative">
              {/* Shadow gradient for scrolling */}
              <div className="absolute top-0 left-0 right-0 h-4 bg-gradient-to-b from-slate-900 to-transparent opacity-0 pointer-events-none z-10 peer-scrolled:opacity-100"></div>

              {activeTab === "appearance" && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="col-span-1 md:col-span-3 mb-6 bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                    <h3 className="text-lg font-medium text-slate-200 mb-4">
                      PDF Header
                    </h3>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Logo
                        </label>
                        <div className="mt-1 flex flex-col sm:flex-row items-start sm:items-center gap-2">
                          {headerLogo && (
                            <div className="h-16 flex items-center bg-slate-700/30 rounded p-2 mb-2 sm:mb-0 sm:mr-4">
                              <img
                                src={headerLogo}
                                alt="Header logo"
                                className="h-12 w-auto"
                              />
                            </div>
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setHeaderLogo(reader.result);
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                            className="text-slate-300 file:mr-4 file:py-2 file:px-4 file:rounded 
                            file:border-0 file:text-sm file:font-medium file:bg-blue-600 
                            file:text-white hover:file:bg-blue-700 file:cursor-pointer
                            bg-slate-800 rounded border border-slate-700 w-full sm:w-auto"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Organization Name
                        </label>
                        <input
                          type="text"
                          value={headerTitle}
                          onChange={(e) => setHeaderTitle(e.target.value)}
                          className="mt-1 block w-full px-3 py-2 bg-slate-800 border border-slate-700 
                          rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-slate-200"
                          placeholder="Enter organization name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Contact Information
                        </label>
                        <textarea
                          value={headerContact}
                          onChange={(e) => setHeaderContact(e.target.value)}
                          rows={3}
                          className="mt-1 block w-full px-3 py-2 bg-slate-800 border border-slate-700 
                          rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-slate-200"
                          placeholder="Address, Phone, Email, etc."
                        />
                        <p className="mt-2 text-xs text-slate-400">
                          This information will appear in your document headers.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="col-span-1 md:col-span-3 mt-2">
                    <h3 className="text-lg font-medium text-slate-200 mb-4">
                      House Colors
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      {Object.entries(selectedColors).map(([house, color]) => (
                        <div
                          key={house}
                          className="space-y-2 bg-slate-800/50 p-3 rounded-lg border border-slate-700/50"
                        >
                          <label className="text-sm font-medium text-slate-300">
                            {house}
                          </label>
                          <div className="relative">
                            <input
                              type="color"
                              value={color}
                              onChange={(e) =>
                                handleColorChange(house, e.target.value)
                              }
                              className="w-full h-10 rounded-lg cursor-pointer bg-slate-800 border border-slate-700"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={handleSaveHouses}
                    className="col-span-1 md:col-span-3 px-4 py-2 mt-6 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <Save size={18} />
                    Save Changes
                  </button>
                </div>
              )}

              {activeTab === "notifications" && <NotificationSettingsTab />}

              {activeTab === "users" && (
                <div className="space-y-6">
                  <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-center gap-2">
                    <div className="relative flex-1 w-full">
                      <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
                      <input
                        type="text"
                        placeholder="Search users..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500"
                      />
                    </div>
                    <select
                      value={filterRole}
                      onChange={(e) => setFilterRole(e.target.value)}
                      className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white w-full sm:w-auto"
                    >
                      <option value="">All Roles</option>
                      {availableRoles.map((role) => (
                        <option key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="p-3 bg-slate-700/70 text-sm rounded-lg text-yellow-200 border border-yellow-500">
                    Note: First Name and Last Name fields are case sensitive.
                    Ensure the exact first and last name that the resident is
                    stored as is also used here when adding them as a user.
                  </div>
                  <form onSubmit={handleAddUser} className="space-y-4">
                    {/* Add User Form */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder="First Name"
                        value={newUserFirstName}
                        onChange={(e) => setNewUserFirstName(e.target.value)}
                        className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500"
                      />
                      <input
                        type="text"
                        placeholder="Last Name"
                        value={newUserLastName}
                        onChange={(e) => setNewUserLastName(e.target.value)}
                        className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                      <input
                        type="email"
                        placeholder="Email address"
                        value={newUserEmail}
                        onChange={(e) => setNewUserEmail(e.target.value)}
                        className="col-span-1 sm:col-span-2 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500"
                      />
                      <select
                        value={newUserRole}
                        onChange={(e) => setNewUserRole(e.target.value)}
                        className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                      >
                        {availableRoles.map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </select>
                      {checkPermission("updateUsers") && (
                        <button
                          type="submit"
                          className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                          <UserPlus size={18} />
                          <span className="hidden sm:inline">Add User</span>
                          <span className="sm:hidden">Add</span>
                        </button>
                      )}
                    </div>
                  </form>

                  {/* Display Users */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-medium text-slate-100">
                        Current Users
                      </h3>
                      <span className="text-sm text-slate-400">
                        Resident Dashboard Limit:{" "}
                        {users.filter((u) => u.role === "Resident").length} /{" "}
                        {residentDashboardCount || 10}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {users
                        .filter(
                          (user) =>
                            (filterRole ? user.role === filterRole : true) &&
                            (user.email
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase()) ||
                              (user.firstName?.toLowerCase() || "").includes(
                                searchTerm.toLowerCase()
                              ) ||
                              (user.lastName?.toLowerCase() || "").includes(
                                searchTerm.toLowerCase()
                              ))
                        )
                        .map((user) => (
                          <div
                            key={user.id}
                            className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-0 sm:justify-between p-3 bg-slate-800 border border-slate-700 rounded-lg sm:space-x-4 hover:bg-slate-700/50 transition-colors"
                          >
                            {/* User Info */}
                            <div className="flex-1 w-full sm:w-auto">
                              <p className="font-medium text-slate-200 text-sm sm:text-base">
                                {user.firstName} {user.lastName}
                              </p>
                              <p className="text-xs sm:text-sm text-slate-400 truncate">
                                {user.email}
                              </p>
                            </div>

                            {/* Role Selection */}
                            <div className="flex flex-row items-center justify-between w-full sm:w-auto sm:flex-none sm:justify-start mt-2 sm:mt-0">
                              <span className="text-xs text-slate-400 sm:hidden">
                                Role:
                              </span>
                              {user.role === "Owner" ? (
                                <span className="text-slate-400 italic text-sm">
                                  Owner
                                </span>
                              ) : checkPermission("updateUserRoles") ? (
                                <select
                                  value={user.role}
                                  // In the role selection onChange handler, replace the existing code with:
                                  onChange={async (e) => {
                                    const newRole = e.target.value;
                                    const normalizedEmail = normalizeEmail(
                                      user.email
                                    );

                                    // Check resident dashboard limit
                                    if (newRole === "Resident") {
                                      const currentResidentCount = users.filter(
                                        (u) => u.role === "Resident"
                                      ).length;
                                      const wouldChangeCount =
                                        user.role === "Resident" ? 0 : 1;
                                      const maxResidents =
                                        residentDashboardCount || 10;

                                      if (
                                        currentResidentCount +
                                          wouldChangeCount >
                                        maxResidents
                                      ) {
                                        setToast({
                                          message: `Cannot change role to Resident. Your limit is ${maxResidents} residents.`,
                                          type: "error",
                                        });
                                        return;
                                      }
                                    }

                                    try {
                                      // Use normalized email for the document reference
                                      const userRef = doc(
                                        db,
                                        "organizations",
                                        organizationId,
                                        "users",
                                        normalizedEmail
                                      );

                                      await updateDoc(userRef, {
                                        role: newRole,
                                      });

                                      // Update local state using normalized email for comparison
                                      setUsers((prevUsers) =>
                                        prevUsers.map((u) =>
                                          normalizeEmail(u.email) ===
                                          normalizedEmail
                                            ? { ...u, role: newRole }
                                            : u
                                        )
                                      );

                                      setToast({
                                        message: `Role updated to ${newRole} successfully.`,
                                        type: "success",
                                      });
                                    } catch (error) {
                                      console.error(
                                        "Error updating user role:",
                                        error
                                      );
                                      setToast({
                                        message:
                                          "Failed to update user role. Please try again.",
                                        type: "error",
                                      });
                                    }
                                  }}
                                  className="ml-2 sm:ml-0 sm:w-40 px-3 py-1 bg-slate-900 text-slate-200 rounded border border-slate-700 focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                                >
                                  {availableRoles.map((role) => (
                                    <option key={role} value={role}>
                                      {role}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <span className="text-slate-400 italic text-xs sm:text-sm">
                                  No permission to update roles
                                </span>
                              )}

                              {/* Delete Button */}
                              {checkPermission("updateUsers") &&
                                user.role !== "Owner" && (
                                  <button
                                    onClick={() => handleDeleteUser(user.email)}
                                    className="ml-auto sm:ml-2 px-3 py-1 text-sm text-red-400 hover:text-red-500 hover:bg-slate-700 rounded transition-colors"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Message Below User Tabs */}
                  <div className="p-4 bg-slate-800/30 border border-slate-700/50 rounded-lg flex items-start gap-3">
                    <AlertCircle className="text-yellow-400 w-5 h-5 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-yellow-300">
                      Once a user is set to owner it can't be changed without
                      approval from the Recovery Home Platform. Reach out if you
                      need an owner's role changed.
                    </p>
                  </div>
                </div>
              )}

              {checkPermission("updatePermissions") &&
                activeTab === "permissions" && (
                  <div className="space-y-4">
                    <div className="space-y-4">
                      {/* For larger screens - table view */}
                      <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg sm:block">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="sticky top-0 bg-slate-800 z-10">
                              <tr>
                                <th className="text-left text-sm font-medium text-slate-400 p-4">
                                  Permission
                                </th>
                                {permissionManagementRoles.map((role) => (
                                  <th
                                    key={role}
                                    className="text-center text-sm font-medium text-slate-400 p-4"
                                  >
                                    {role}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700/50">
                              {/* RESIDENTS & PROFILES */}
                              <tr
                                className="bg-slate-700 cursor-pointer hover:bg-slate-600/70"
                                onClick={() =>
                                  setExpandedSection(
                                    expandedSection === "residents"
                                      ? null
                                      : "residents"
                                  )
                                }
                              >
                                <td
                                  colSpan="4"
                                  className="p-4 text-left font-medium text-slate-300"
                                >
                                  <div className="flex items-center justify-between">
                                    <span>Residents & Profiles</span>
                                    {expandedSection === "residents" ? (
                                      <ChevronUp size={18} />
                                    ) : (
                                      <ChevronDown size={18} />
                                    )}
                                  </div>
                                </td>
                              </tr>
                              {expandedSection === "residents" &&
                                [
                                  {
                                    key: "canAddResidents",
                                    label: "Add Residents",
                                    tooltip:
                                      "This allows a user to submit a new resident or add them to the waitlist through the intake form.",
                                  },
                                  {
                                    key: "canApproveDenyOvernight",
                                    label: "Can Approve/Deny Overnight Passes",
                                    tooltip:
                                      "This allows a user to approve or deny overnight passes for residents.",
                                  },
                                  {
                                    key: "viewResidents",
                                    label: "View Residents",
                                    tooltip:
                                      "Access the list of all residents currently enrolled in the program.",
                                  },
                                  {
                                    key: "seeApplications",
                                    label: "View Applications",
                                    tooltip:
                                      "Access the applications page which shows all incoming and past applications.",
                                  },
                                  {
                                    key: "viewWaitlist",
                                    label: "View Waitlist",
                                    tooltip:
                                      "Access the list of prospective residents on the waitlist.",
                                  },
                                  {
                                    key: "viewSponsorship",
                                    label: "View Sponsorship",
                                    tooltip:
                                      "Access and update the sponsorship page.",
                                  },
                                  {
                                    key: "dischargeResidents",
                                    label: "Discharge Residents",
                                    tooltip:
                                      "This determines who is eligible to discharge residents.",
                                  },
                                  {
                                    key: "viewProfile",
                                    label: "View Resident Profiles",
                                    tooltip:
                                      "Access detailed resident information including personal data.",
                                  },
                                  {
                                    key: "viewPhases",
                                    label: "View Phases Page",
                                    tooltip:
                                      "Access the page where you can update and customize phase requirements associated with your program.",
                                  },
                                  {
                                    key: "addForms",
                                    label: "Add Resident Documents",
                                    tooltip:
                                      "Create and submit new forms for individual resident files.",
                                  },
                                  {
                                    key: "createForms",
                                    label: "Create Custom Forms",
                                    tooltip:
                                      "Create custom forms for your organization.",
                                  },
                                  {
                                    key: "updateResidentDoc",
                                    label: "Update Resident Documents",
                                    tooltip:
                                      "Update existing individual resident files.",
                                  },
                                  {
                                    key: "deleteResidentDoc",
                                    label: "Delete Resident Documents",
                                    tooltip:
                                      "Delete existing individual resident files.",
                                  },
                                  {
                                    key: "viewPaymentHistory",
                                    label: "View Payment History",
                                    tooltip:
                                      "Access records of resident payments, including payment dates, amounts, and outstanding balances.",
                                  },
                                  {
                                    key: "updateResidentInfo",
                                    label: "Update Resident Info",
                                    tooltip:
                                      "Modify resident personal information, contact details, and program-related data.",
                                  },
                                  {
                                    key: "viewResidentDashboard",
                                    label: "View Resident Dashboard",
                                    tooltip:
                                      "This provides the ability to view specfic resident dashboards so you can see exactly what the residents see on their end.",
                                  },
                                  {
                                    key: "exportResidentData",
                                    label: "Export Resident Data",
                                    tooltip:
                                      "Download resident information for external use, reporting, or record-keeping purposes.",
                                  },
                                  {
                                    key: "updateSchedule",
                                    label: "Update Resident Schedule",
                                    tooltip:
                                      "This permission is for users to add and update the schedule.",
                                  },
                                ].map(({ key, label, tooltip }) => (
                                  <tr key={key}>
                                    <td className="py-3 px-4 text-slate-300">
                                      <div className="flex items-center space-x-2 group relative">
                                        <span>{label}</span>
                                        <InfoIcon className="w-4 h-4 text-slate-400 cursor-help" />
                                        <div className="invisible group-hover:visible absolute left-0 -top-16 bg-slate-900 border border-slate-700 rounded-lg shadow-lg p-4 text-slate-300 w-64 z-50">
                                          {tooltip}
                                        </div>
                                      </div>
                                    </td>
                                    {["Admin", "Staff", "Leader"].map(
                                      (role) => (
                                        <td
                                          key={role}
                                          className="text-center py-3"
                                        >
                                          <input
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-slate-600 bg-slate-800"
                                            checked={
                                              permissions?.[role]?.[key] ??
                                              false
                                            }
                                            onChange={(e) =>
                                              handlePermissionsChange(
                                                role,
                                                key,
                                                e.target.checked
                                              )
                                            }
                                          />
                                        </td>
                                      )
                                    )}
                                  </tr>
                                ))}

                              {/* ANALYTICS */}
                              <tr
                                className="bg-slate-700 cursor-pointer hover:bg-slate-600/70"
                                onClick={() =>
                                  setExpandedSection(
                                    expandedSection === "analytics"
                                      ? null
                                      : "analytics"
                                  )
                                }
                              >
                                <td
                                  colSpan="4"
                                  className="p-4 text-left font-medium text-slate-300"
                                >
                                  <div className="flex items-center justify-between">
                                    <span>Analytics</span>
                                    {expandedSection === "analytics" ? (
                                      <ChevronUp size={18} />
                                    ) : (
                                      <ChevronDown size={18} />
                                    )}
                                  </div>
                                </td>
                              </tr>
                              {expandedSection === "analytics" &&
                                [
                                  {
                                    key: "viewAnalytics",
                                    label: "View Analytics",
                                    tooltip:
                                      "Access and view the analytics page.",
                                  },
                                  {
                                    key: "saveSnapshots",
                                    label: "Save Analytics Snapshots",
                                    tooltip:
                                      "Create and save point-in-time captures of analytics data for historical comparison and reporting purposes.",
                                  },
                                  {
                                    key: "canViewAnalyticData",
                                    label: "View Analytic Data",
                                    tooltip:
                                      "Allows access to detailed historical analytic data for review and analysis.",
                                  },
                                ].map(({ key, label, tooltip }) => (
                                  <tr key={key}>
                                    <td className="py-3 px-4 text-slate-300">
                                      <div className="flex items-center space-x-2 group relative">
                                        <span>{label}</span>
                                        <InfoIcon className="w-4 h-4 text-slate-400 cursor-help" />
                                        <div className="invisible group-hover:visible absolute left-0 -top-16 bg-slate-900 border border-slate-700 rounded-lg shadow-lg p-4 text-slate-300 w-64 z-50">
                                          {tooltip}
                                        </div>
                                      </div>
                                    </td>
                                    {["Admin", "Staff", "Leader"].map(
                                      (role) => (
                                        <td
                                          key={role}
                                          className="text-center py-3"
                                        >
                                          <input
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-slate-600 bg-slate-800"
                                            checked={
                                              permissions?.[role]?.[key] ??
                                              defaultPermissions[role][key]
                                            }
                                            onChange={(e) =>
                                              handlePermissionsChange(
                                                role,
                                                key,
                                                e.target.checked
                                              )
                                            }
                                          />
                                        </td>
                                      )
                                    )}
                                  </tr>
                                ))}

                              {/* RECOVERY CAPITAL */}
                              <tr
                                className="bg-slate-700 cursor-pointer hover:bg-slate-600/70"
                                onClick={() =>
                                  setExpandedSection(
                                    expandedSection === "recovery"
                                      ? null
                                      : "recovery"
                                  )
                                }
                              >
                                <td
                                  colSpan="4"
                                  className="p-4 text-left font-medium text-slate-300"
                                >
                                  <div className="flex items-center justify-between">
                                    <span>Recovery Capital</span>
                                    {expandedSection === "recovery" ? (
                                      <ChevronUp size={18} />
                                    ) : (
                                      <ChevronDown size={18} />
                                    )}
                                  </div>
                                </td>
                              </tr>
                              {expandedSection === "recovery" &&
                                [
                                  {
                                    key: "viewRecoveryCapital",
                                    label: "View Recovery Capital",
                                    tooltip:
                                      "Access and view the recovery capital page.",
                                  },
                                  {
                                    key: "addRecoveryCapital",
                                    label: "Add Recovery Capital",
                                    tooltip:
                                      "Submit new recovery capital data for residents.",
                                  },
                                  {
                                    key: "deleteToDo",
                                    label: "Delete To-Do Recovery Capital",
                                    tooltip:
                                      "Remove residents from the to-do list on the recovery capital page.",
                                  },
                                  {
                                    key: "exportRecoveryData",
                                    label: "Export Recovery Capital Data",
                                    tooltip:
                                      "Download and export recovery capital data for external use, reporting, or analysis.",
                                  },
                                ].map(({ key, label, tooltip }) => (
                                  <tr key={key}>
                                    <td className="py-3 px-4 text-slate-300">
                                      <div className="flex items-center space-x-2 group relative">
                                        <span>{label}</span>
                                        <InfoIcon className="w-4 h-4 text-slate-400 cursor-help" />
                                        <div className="invisible group-hover:visible absolute left-0 -top-16 bg-slate-900 border border-slate-700 rounded-lg shadow-lg p-4 text-slate-300 w-64 z-50">
                                          {tooltip}
                                        </div>
                                      </div>
                                    </td>
                                    {["Admin", "Staff", "Leader"].map(
                                      (role) => (
                                        <td
                                          key={role}
                                          className="text-center py-3"
                                        >
                                          <input
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-slate-600 bg-slate-800"
                                            checked={
                                              permissions?.[role]?.[key] ??
                                              defaultPermissions[role][key]
                                            }
                                            onChange={(e) =>
                                              handlePermissionsChange(
                                                role,
                                                key,
                                                e.target.checked
                                              )
                                            }
                                          />
                                        </td>
                                      )
                                    )}
                                  </tr>
                                ))}

                              {/* DASHBOARD */}
                              <tr
                                className="bg-slate-700 cursor-pointer hover:bg-slate-600/70"
                                onClick={() =>
                                  setExpandedSection(
                                    expandedSection === "dashboard"
                                      ? null
                                      : "dashboard"
                                  )
                                }
                              >
                                <td
                                  colSpan="4"
                                  className="p-4 text-left font-medium text-slate-300"
                                >
                                  <div className="flex items-center justify-between">
                                    <span>Dashboard</span>
                                    {expandedSection === "dashboard" ? (
                                      <ChevronUp size={18} />
                                    ) : (
                                      <ChevronDown size={18} />
                                    )}
                                  </div>
                                </td>
                              </tr>
                              {expandedSection === "dashboard" &&
                                [
                                  {
                                    key: "manageBanList",
                                    label: "Manage Ban List",
                                    tooltip:
                                      "Modify entries in the banned individuals list on the dashboard page.",
                                  },
                                  {
                                    key: "manageUACounts",
                                    label: "Manage UA Counts",
                                    tooltip:
                                      "Adjust UA counts from the dashboard page.",
                                  },
                                  {
                                    key: "accessOwnerDashboard",
                                    label: "Access Owner Dashboard",
                                    tooltip:
                                      "Provides access to the owner's dashboard with high-level program management tools and financial information.",
                                  },
                                ].map(({ key, label, tooltip }) => (
                                  <tr key={key}>
                                    <td className="py-3 px-4 text-slate-300">
                                      <div className="flex items-center space-x-2 group relative">
                                        <span>{label}</span>
                                        <InfoIcon className="w-4 h-4 text-slate-400 cursor-help" />
                                        <div className="invisible group-hover:visible absolute left-0 -top-16 bg-slate-900 border border-slate-700 rounded-lg shadow-lg p-4 text-slate-300 w-64 z-50">
                                          {tooltip}
                                        </div>
                                      </div>
                                    </td>
                                    {["Admin", "Staff", "Leader"].map(
                                      (role) => (
                                        <td
                                          key={role}
                                          className="text-center py-3"
                                        >
                                          <input
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-slate-600 bg-slate-800"
                                            checked={
                                              permissions?.[role]?.[key] ??
                                              defaultPermissions[role][key]
                                            }
                                            onChange={(e) =>
                                              handlePermissionsChange(
                                                role,
                                                key,
                                                e.target.checked
                                              )
                                            }
                                          />
                                        </td>
                                      )
                                    )}
                                  </tr>
                                ))}

                              {/* ALUMNI */}
                              <tr
                                className="bg-slate-700 cursor-pointer hover:bg-slate-600/70"
                                onClick={() =>
                                  setExpandedSection(
                                    expandedSection === "alumni"
                                      ? null
                                      : "alumni"
                                  )
                                }
                              >
                                <td
                                  colSpan="4"
                                  className="p-4 text-left font-medium text-slate-300"
                                >
                                  <div className="flex items-center justify-between">
                                    <span>Alumni</span>
                                    {expandedSection === "alumni" ? (
                                      <ChevronUp size={18} />
                                    ) : (
                                      <ChevronDown size={18} />
                                    )}
                                  </div>
                                </td>
                              </tr>
                              {expandedSection === "alumni" &&
                                [
                                  {
                                    key: "viewAlumni",
                                    label: "View Alumni",
                                    tooltip:
                                      "Access the list of program graduates.",
                                  },
                                  {
                                    key: "viewAlumniProfile",
                                    label: "View Alumni Profiles",
                                    tooltip:
                                      "Access detailed information about program graduates, including discharge info.",
                                  },
                                  {
                                    key: "recordAlumniEvents",
                                    label: "Record Alumni Events",
                                    tooltip:
                                      "Record events about program graduates, including relapses or re arrests post discharge.",
                                  },
                                ].map(({ key, label, tooltip }) => (
                                  <tr key={key}>
                                    <td className="py-3 px-4 text-slate-300">
                                      <div className="flex items-center space-x-2 group relative">
                                        <span>{label}</span>
                                        <InfoIcon className="w-4 h-4 text-slate-400 cursor-help" />
                                        <div className="invisible group-hover:visible absolute left-0 -top-16 bg-slate-900 border border-slate-700 rounded-lg shadow-lg p-4 text-slate-300 w-64 z-50">
                                          {tooltip}
                                        </div>
                                      </div>
                                    </td>
                                    {["Admin", "Staff", "Leader"].map(
                                      (role) => (
                                        <td
                                          key={role}
                                          className="text-center py-3"
                                        >
                                          <input
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-slate-600 bg-slate-800"
                                            checked={
                                              permissions?.[role]?.[key] ??
                                              defaultPermissions[role][key]
                                            }
                                            onChange={(e) =>
                                              handlePermissionsChange(
                                                role,
                                                key,
                                                e.target.checked
                                              )
                                            }
                                          />
                                        </td>
                                      )
                                    )}
                                  </tr>
                                ))}

                              {/* BEDS */}
                              <tr
                                className="bg-slate-700 cursor-pointer hover:bg-slate-600/70"
                                onClick={() =>
                                  setExpandedSection(
                                    expandedSection === "beds" ? null : "beds"
                                  )
                                }
                              >
                                <td
                                  colSpan="4"
                                  className="p-4 text-left font-medium text-slate-300"
                                >
                                  <div className="flex items-center justify-between">
                                    <span>Beds</span>
                                    {expandedSection === "beds" ? (
                                      <ChevronUp size={18} />
                                    ) : (
                                      <ChevronDown size={18} />
                                    )}
                                  </div>
                                </td>
                              </tr>
                              {expandedSection === "beds" &&
                                [
                                  {
                                    key: "viewBeds",
                                    label: "View Bed Platform",
                                    tooltip: "Access the bed platform page.",
                                  },
                                  {
                                    key: "updateBeds",
                                    label: "Update Beds",
                                    tooltip:
                                      "Modify bed assignments, mark beds as available/unavailable.",
                                  },
                                ].map(({ key, label, tooltip }) => (
                                  <tr key={key}>
                                    <td className="py-3 px-4 text-slate-300">
                                      <div className="flex items-center space-x-2 group relative">
                                        <span>{label}</span>
                                        <InfoIcon className="w-4 h-4 text-slate-400 cursor-help" />
                                        <div className="invisible group-hover:visible absolute left-0 -top-16 bg-slate-900 border border-slate-700 rounded-lg shadow-lg p-4 text-slate-300 w-64 z-50">
                                          {tooltip}
                                        </div>
                                      </div>
                                    </td>
                                    {["Admin", "Staff", "Leader"].map(
                                      (role) => (
                                        <td
                                          key={role}
                                          className="text-center py-3"
                                        >
                                          <input
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-slate-600 bg-slate-800"
                                            checked={
                                              permissions?.[role]?.[key] ??
                                              defaultPermissions[role][key]
                                            }
                                            onChange={(e) =>
                                              handlePermissionsChange(
                                                role,
                                                key,
                                                e.target.checked
                                              )
                                            }
                                          />
                                        </td>
                                      )
                                    )}
                                  </tr>
                                ))}

                              {/* UAs */}
                              <tr
                                className="bg-slate-700 cursor-pointer hover:bg-slate-600/70"
                                onClick={() =>
                                  setExpandedSection(
                                    expandedSection === "uas" ? null : "uas"
                                  )
                                }
                              >
                                <td
                                  colSpan="4"
                                  className="p-4 text-left font-medium text-slate-300"
                                >
                                  <div className="flex items-center justify-between">
                                    <span>Urinalysis (UAs)</span>
                                    {expandedSection === "uas" ? (
                                      <ChevronUp size={18} />
                                    ) : (
                                      <ChevronDown size={18} />
                                    )}
                                  </div>
                                </td>
                              </tr>
                              {expandedSection === "uas" &&
                                [
                                  {
                                    key: "viewUa",
                                    label: "View UA Platform",
                                    tooltip:
                                      "Access the urinalysis testing platform, including scheduled tests.",
                                  },
                                  {
                                    key: "addUa",
                                    label: "Add UAs",
                                    tooltip:
                                      "Record new urinalysis test results.",
                                  },
                                  {
                                    key: "viewUaHistory",
                                    label: "View UA History",
                                    tooltip:
                                      "Access historical urinalysis test results and testing patterns for residents.",
                                  },
                                ].map(({ key, label, tooltip }) => (
                                  <tr key={key}>
                                    <td className="py-3 px-4 text-slate-300">
                                      <div className="flex items-center space-x-2 group relative">
                                        <span>{label}</span>
                                        <InfoIcon className="w-4 h-4 text-slate-400 cursor-help" />
                                        <div className="invisible group-hover:visible absolute left-0 -top-16 bg-slate-900 border border-slate-700 rounded-lg shadow-lg p-4 text-slate-300 w-64 z-50">
                                          {tooltip}
                                        </div>
                                      </div>
                                    </td>
                                    {["Admin", "Staff", "Leader"].map(
                                      (role) => (
                                        <td
                                          key={role}
                                          className="text-center py-3"
                                        >
                                          <input
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-slate-600 bg-slate-800"
                                            checked={
                                              permissions?.[role]?.[key] ??
                                              defaultPermissions[role][key]
                                            }
                                            onChange={(e) =>
                                              handlePermissionsChange(
                                                role,
                                                key,
                                                e.target.checked
                                              )
                                            }
                                          />
                                        </td>
                                      )
                                    )}
                                  </tr>
                                ))}

                              {/* SETTINGS */}
                              <tr
                                className="bg-slate-700 cursor-pointer hover:bg-slate-600/70"
                                onClick={() =>
                                  setExpandedSection(
                                    expandedSection === "settings"
                                      ? null
                                      : "settings"
                                  )
                                }
                              >
                                <td
                                  colSpan="4"
                                  className="p-4 text-left font-medium text-slate-300"
                                >
                                  <div className="flex items-center justify-between">
                                    <span>Settings</span>
                                    {expandedSection === "settings" ? (
                                      <ChevronUp size={18} />
                                    ) : (
                                      <ChevronDown size={18} />
                                    )}
                                  </div>
                                </td>
                              </tr>
                              {expandedSection === "settings" &&
                                [
                                  {
                                    key: "viewSettings",
                                    label: "View Settings",
                                    tooltip:
                                      "Allows the user to view the setting modal.",
                                  },
                                  {
                                    key: "updatePermissions",
                                    label: "Update Permissions",
                                    tooltip:
                                      "Allows the user to modify user permissions in the settings modal.",
                                  },
                                  {
                                    key: "updateUsers",
                                    label: "Update Users",
                                    tooltip:
                                      "Allows the user to add, update, or remove organization users.",
                                  },
                                  {
                                    key: "updateHouseSettings",
                                    label: "Update House Settings",
                                    tooltip:
                                      "Allows the user to modify house-specific settings in the settings modal.",
                                  },
                                  {
                                    key: "updateUserRoles",
                                    label: "Update User Roles",
                                    tooltip:
                                      "Allows the user to assign or update user roles.",
                                  },
                                ].map(({ key, label, tooltip }) => (
                                  <tr key={key}>
                                    <td className="py-3 px-4 text-slate-300">
                                      <div className="flex items-center space-x-2 group relative">
                                        <span>{label}</span>
                                        <InfoIcon className="w-4 h-4 text-slate-400 cursor-help" />
                                        <div className="invisible group-hover:visible absolute left-0 -top-16 bg-slate-900 border border-slate-700 rounded-lg shadow-lg p-4 text-slate-300 w-64 z-50">
                                          {tooltip}
                                        </div>
                                      </div>
                                    </td>
                                    {["Admin", "Staff", "Leader"].map(
                                      (role) => (
                                        <td
                                          key={role}
                                          className="text-center py-3"
                                        >
                                          <input
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-slate-600 bg-slate-800"
                                            checked={
                                              permissions?.[role]?.[key] ??
                                              defaultPermissions[role][key]
                                            }
                                            onChange={(e) =>
                                              handlePermissionsChange(
                                                role,
                                                key,
                                                e.target.checked
                                              )
                                            }
                                          />
                                        </td>
                                      )
                                    )}
                                  </tr>
                                ))}

                              {/* PAYMENTS & INVOICES */}
                              <tr
                                className="bg-slate-700 cursor-pointer hover:bg-slate-600/70"
                                onClick={() =>
                                  setExpandedSection(
                                    expandedSection === "payments"
                                      ? null
                                      : "payments"
                                  )
                                }
                              >
                                <td
                                  colSpan="4"
                                  className="p-4 text-left font-medium text-slate-300"
                                >
                                  <div className="flex items-center justify-between">
                                    <span>Payments & Invoices</span>
                                    {expandedSection === "payments" ? (
                                      <ChevronUp size={18} />
                                    ) : (
                                      <ChevronDown size={18} />
                                    )}
                                  </div>
                                </td>
                              </tr>
                              {expandedSection === "payments" &&
                                [
                                  {
                                    key: "viewPayments",
                                    label: "View Payments",
                                    tooltip:
                                      "Access payment records, including transaction history and payment status of all residents.",
                                  },
                                  {
                                    key: "addPayment",
                                    label: "Add Payment",
                                    tooltip:
                                      "Record new payments received from residents or third-party payers.",
                                  },
                                  {
                                    key: "updatePayments",
                                    label: "Update Payments",
                                    tooltip:
                                      "Modify payment information for residents such as due dates and housing fee amounts.",
                                  },
                                  {
                                    key: "viewInvoice",
                                    label: "View Invoices",
                                    tooltip:
                                      "Access invoice page where you can view all organization invoices.",
                                  },
                                  {
                                    key: "updateInvoices",
                                    label: "Update Invoices",
                                    tooltip:
                                      "Modify existing invoices by marking them as paid or unpaid.",
                                  },
                                ].map(({ key, label, tooltip }) => (
                                  <tr key={key}>
                                    <td className="py-3 px-4 text-slate-300">
                                      <div className="flex items-center space-x-2 group relative">
                                        <span>{label}</span>
                                        <InfoIcon className="w-4 h-4 text-slate-400 cursor-help" />
                                        <div className="invisible group-hover:visible absolute left-0 -top-16 bg-slate-900 border border-slate-700 rounded-lg shadow-lg p-4 text-slate-300 w-64 z-50">
                                          {tooltip}
                                        </div>
                                      </div>
                                    </td>
                                    {["Admin", "Staff", "Leader"].map(
                                      (role) => (
                                        <td
                                          key={role}
                                          className="text-center py-3"
                                        >
                                          <input
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-slate-600 bg-slate-800"
                                            checked={
                                              permissions?.[role]?.[key] ??
                                              defaultPermissions[role][key]
                                            }
                                            onChange={(e) =>
                                              handlePermissionsChange(
                                                role,
                                                key,
                                                e.target.checked
                                              )
                                            }
                                          />
                                        </td>
                                      )
                                    )}
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                          <button
                            onClick={handleSavePermissions}
                            className="w-full px-4 py-2 mt-4 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center justify-center gap-2"
                          >
                            <Shield size={18} />
                            Save Permissions
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              {activeTab === "features" && (
                <div className="space-y-6">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-1 sm:gap-0">
                    <div className="flex items-center gap-2">
                      <Layers className="w-5 h-5 text-blue-500" />
                      <h3 className="text-lg font-medium text-white">
                        Platform Features
                      </h3>
                    </div>
                    <p className="text-sm text-slate-400">
                      Enable or disable features for your organization
                    </p>
                  </div>

                  <div className="space-y-4 bg-slate-800/50 rounded-lg p-3 sm:p-4">
                    <div className="p-3 sm:p-4 bg-slate-800/30 border border-slate-700/50 rounded-lg flex items-start gap-3">
                      <InfoIcon className="text-yellow-400 w-5 h-5 flex-shrink-0 mt-0.5" />
                      <p className="text-xs sm:text-sm text-yellow-300">
                        Disabling a feature will hide it from all users in your
                        organization. This won't delete any data associated with
                        the feature. You can change these back and forth as you
                        see fit.
                      </p>
                    </div>

                    {/* Staff Dashboard Features */}
                    <div className="mt-6">
                      <h4 className="text-base sm:text-lg font-medium text-white mb-4 flex items-center gap-2">
                        <User className="w-5 h-5 text-blue-400" />
                        Staff Dashboard Features
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {features.staff &&
                          Object.entries(features.staff).map(
                            ([feature, enabled]) => {
                              // Feature tooltips mapping for staff
                              const tooltips = {
                                customForms:
                                  "When disabled, you will be unable to create organization documents or applications for residents to fill out.",
                                sponsorship:
                                  "The sponsorship page will disappear when left unchecked.",
                                bedPlatform:
                                  "When disabled, the bed platform will be removed.",
                                records:
                                  "When disabled, the records card will be removed on the dashboard.",
                                uaScheduling:
                                  "If you don't do a routine schedule of weekly UA's, this should be left unchecked. If this and the UA Recording are both unchecked, it will remove the UA functionality from your platform entirely.",
                                uaRecording:
                                  "This will prevent you from recording UA's. If this box, and the UA Scheduling box is unchecked, the UA page will be removed from your platform.",
                                analytics:
                                  "When disabled, you will not have an analytics page, although they will still track in the background in case you change your mind in the future.",
                                applications:
                                  "This should be left unchecked if you do your applications outside of the custom forms that get built in this system.",
                                ownerDashboard:
                                  'When unchecked, the overview page will be removed. This prevents you from sending notifications to other users as well as adding "Important Documents" which show up on the Dashboard.',
                                recoveryCapital:
                                  "Enables the recovery capital assessment and tracking feature.",
                                payments:
                                  "Enables payment and invoicing functionality for resident fees.",
                                phases:
                                  "Enables the phase system for tracking resident progress.",
                                schedule:
                                  "Enables the schedule feature for staff to manage resident schedules and events.",
                              };

                              return (
                                <div
                                  key={`staff-${feature}`}
                                  className="flex flex-col sm:flex-row sm:items-center p-3 sm:p-4 bg-slate-800/70 rounded-lg hover:bg-slate-700/50 transition-colors"
                                >
                                  <div className="flex-1 mb-2 sm:mb-0">
                                    <div className="flex items-center mb-1">
                                      <span className="font-medium text-slate-200 text-sm sm:text-base">
                                        {getFeatureLabel(feature)}
                                      </span>
                                      <div className="relative">
                                        <InfoIcon
                                          className="w-4 h-4 text-slate-400 cursor-help ml-2"
                                          onMouseEnter={() =>
                                            setHoveredFeature(
                                              `staff-${feature}`
                                            )
                                          }
                                          onMouseLeave={() =>
                                            setHoveredFeature(null)
                                          }
                                        />
                                        {hoveredFeature ===
                                          `staff-${feature}` && (
                                          <div className="absolute left-0 -top-2 -translate-y-full bg-slate-900 border border-slate-700 rounded-lg shadow-lg p-3 text-slate-300 w-64 z-50">
                                            {tooltips[feature]}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <p className="text-xs text-slate-400 line-clamp-2 sm:line-clamp-1">
                                      {tooltips[feature]}
                                    </p>
                                  </div>
                                  <label className="relative inline-flex items-center cursor-pointer mt-1 sm:mt-0 sm:ml-4">
                                    <input
                                      type="checkbox"
                                      checked={enabled}
                                      onChange={() =>
                                        handleFeatureToggle(feature, "staff")
                                      }
                                      className="sr-only peer"
                                    />
                                    <div className="w-10 h-5 sm:w-12 sm:h-6 bg-slate-600 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 sm:after:h-5 sm:after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                                  </label>
                                </div>
                              );
                            }
                          )}
                      </div>
                    </div>

                    {/* Resident Dashboard Features */}
                    <div className="mt-6">
                      <h4 className="text-base sm:text-lg font-medium text-white mb-4 flex items-center gap-2">
                        <Users className="w-5 h-5 text-blue-400" />
                        Resident Dashboard Features
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {features.resident &&
                          Object.entries(features.resident).map(
                            ([feature, enabled]) => {
                              // Feature tooltips mapping for resident dashboard
                              const tooltips = {
                                overnightRequest:
                                  "When enabled, residents can submit overnight stay requests which can be approved or denied by staff.",
                                payments:
                                  "Enables residents to view their payment history. This will be disabled if the Staff Payments feature is turned off.",
                                phases:
                                  "Enables residents to view their phase progress. This will be disabled if the Staff Phase System feature is turned off.",
                                recoveryCapital:
                                  "Allows residents to view their recovery capital assessments. This will be disabled if the Staff Recovery Capital feature is turned off.",
                                ua: "Enables residents to view their UA results. This will be disabled if either UA Recording or UA Scheduling is turned off for staff.",
                                geoLocation:
                                  "Enables location tracking for resident check-ins and meeting attendance verification.",
                                schedule:
                                  "Allows residents to view their personal schedule and upcoming events.",
                              };

                              // Determine if this feature should be disabled based on staff settings
                              let isDisabled = false;
                              let disabledMessage = "";

                              if (
                                feature === "payments" &&
                                !features.staff.payments
                              ) {
                                isDisabled = true;
                                disabledMessage =
                                  "This requires Staff Payments to be enabled";
                              } else if (
                                feature === "phases" &&
                                !features.staff.phases
                              ) {
                                isDisabled = true;
                                disabledMessage =
                                  "This requires Staff Phase System to be enabled";
                              } else if (
                                feature === "recoveryCapital" &&
                                !features.staff.recoveryCapital
                              ) {
                                isDisabled = true;
                                disabledMessage =
                                  "This requires Staff Recovery Capital to be enabled";
                              } else if (
                                feature === "ua" &&
                                (!features.staff.uaRecording ||
                                  !features.staff.uaScheduling)
                              ) {
                                isDisabled = true;
                                disabledMessage =
                                  "This requires both Staff UA Recording and UA Scheduling to be enabled";
                              }

                              return (
                                <div
                                  key={`resident-${feature}`}
                                  className={`flex flex-col sm:flex-row sm:items-center p-3 sm:p-4 rounded-lg ${
                                    isDisabled
                                      ? "bg-slate-700/30 opacity-60"
                                      : "bg-slate-800/70 hover:bg-slate-700/50"
                                  } transition-colors`}
                                >
                                  <div className="flex-1 mb-2 sm:mb-0">
                                    <div className="flex items-center mb-1">
                                      <span className="font-medium text-slate-200 text-sm sm:text-base">
                                        {getFeatureLabel(feature)}
                                      </span>
                                      <div className="relative">
                                        <InfoIcon
                                          className="w-4 h-4 text-slate-400 cursor-help ml-2"
                                          onMouseEnter={() =>
                                            setHoveredFeature(
                                              `resident-${feature}`
                                            )
                                          }
                                          onMouseLeave={() =>
                                            setHoveredFeature(null)
                                          }
                                        />
                                        {hoveredFeature ===
                                          `resident-${feature}` && (
                                          <div className="absolute left-0 -top-2 -translate-y-full bg-slate-900 border border-slate-700 rounded-lg shadow-lg p-3 text-slate-300 w-64 z-50">
                                            {isDisabled
                                              ? disabledMessage
                                              : tooltips[feature]}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <p className="text-xs text-slate-400 line-clamp-2 sm:line-clamp-1">
                                      {isDisabled
                                        ? disabledMessage
                                        : tooltips[feature]}
                                    </p>
                                  </div>
                                  <label className="relative inline-flex items-center cursor-pointer mt-1 sm:mt-0 sm:ml-4">
                                    <input
                                      type="checkbox"
                                      checked={enabled}
                                      onChange={() =>
                                        !isDisabled &&
                                        handleFeatureToggle(feature, "resident")
                                      }
                                      className="sr-only peer"
                                      disabled={isDisabled}
                                    />
                                    <div
                                      className={`w-10 h-5 sm:w-12 sm:h-6 ${
                                        isDisabled
                                          ? "bg-slate-700"
                                          : "bg-slate-600"
                                      } rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 sm:after:h-5 sm:after:w-5 after:transition-all peer-checked:bg-blue-500 peer-disabled:opacity-50`}
                                    ></div>
                                  </label>
                                </div>
                              );
                            }
                          )}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleSaveFeatures}
                    className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <Save size={18} />
                    Save Feature Settings
                  </button>
                </div>
              )}

              {checkPermission("updateHouseSettings") &&
                activeTab === "houses" && (
                  <div className="space-y-6 overflow-y-auto max-h-full">
                    {/* Move-In Fee */}
                    <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-3 sm:p-4 mb-4">
                      <div className="flex items-center gap-3 mb-3">
                        <DollarSign
                          className="text-green-500 flex-shrink-0"
                          size={20}
                        />
                        <h3 className="text-base sm:text-lg font-medium text-slate-100">
                          Move-In Fee
                        </h3>
                      </div>
                      <p className="text-xs sm:text-sm text-slate-400 mb-3">
                        This one-time fee applies to all houses in your
                        organization when a resident moves in.
                      </p>
                      <div className="flex items-center">
                        <span className="text-slate-300 mr-2">$</span>
                        <input
                          type="number"
                          value={moveInFee}
                          onChange={(e) =>
                            handleMoveInFeeChange(e.target.value)
                          }
                          className="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-slate-900/50 rounded-lg border border-slate-700 focus:border-green-500 focus:ring-1 focus:ring-green-500 text-white"
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>

                    {/* Existing Houses */}
                    <div className="space-y-4">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <Building2
                            className="text-blue-500 flex-shrink-0"
                            size={24}
                          />
                          <h2 className="text-lg sm:text-xl font-semibold text-white">
                            Houses Configuration
                          </h2>
                        </div>
                        <button
                          onClick={() => setIsAddingHouse(true)}
                          className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors w-full sm:w-auto"
                        >
                          <Plus size={18} />
                          Add House
                        </button>
                      </div>

                      {houses.map((house, houseIndex) => (
                        <div
                          key={houseIndex}
                          className="bg-slate-800/50 border border-slate-700/50 rounded-lg overflow-hidden"
                        >
                          <div
                            className="flex items-center justify-between p-3 sm:p-4 cursor-pointer hover:bg-slate-700/30 transition-colors"
                            onClick={() =>
                              setExpandedHouse(
                                expandedHouse === houseIndex ? null : houseIndex
                              )
                            }
                          >
                            <div className="flex items-center">
                              <div
                                className="w-6 h-6 sm:w-8 sm:h-8 rounded-full mr-2 sm:mr-3 flex-shrink-0"
                                style={{
                                  backgroundColor:
                                    house.color || getDefaultColor(houseIndex),
                                }}
                              ></div>
                              <div>
                                <h3 className="text-base sm:text-lg font-medium text-white">
                                  {house.name}
                                </h3>
                                <p className="text-xs sm:text-sm text-slate-400">
                                  ${house.fee}/week ·{" "}
                                  {calculateTotalBeds([house])} beds
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <div className="relative">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation(); // Stop event propagation
                                    // Find and click the hidden color input
                                    e.currentTarget.nextElementSibling.click();
                                  }}
                                  className="p-2 text-slate-400 hover:text-slate-300 hover:bg-slate-700 rounded-lg"
                                >
                                  <Palette size={18} />
                                </button>
                                <input
                                  type="color"
                                  value={
                                    house.color || getDefaultColor(houseIndex)
                                  }
                                  onChange={(e) => {
                                    handleColorChange(
                                      house.name,
                                      e.target.value
                                    );
                                  }}
                                  className="absolute opacity-0 w-0 h-0 pointer-events-none"
                                  aria-hidden="true"
                                />
                              </div>
                              {expandedHouse === houseIndex ? (
                                <ChevronUp
                                  size={18}
                                  className="text-slate-400"
                                />
                              ) : (
                                <ChevronDown
                                  size={18}
                                  className="text-slate-400"
                                />
                              )}
                            </div>
                          </div>

                          {expandedHouse === houseIndex && (
                            <div className="p-3 sm:p-4 border-t border-slate-700/50 space-y-4 sm:space-y-6">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <label className="block text-sm font-medium text-slate-300">
                                    House Name
                                  </label>
                                  <input
                                    type="text"
                                    value={house.name}
                                    onChange={(e) =>
                                      handleHouseChange(
                                        houseIndex,
                                        "name",
                                        e.target.value
                                      )
                                    }
                                    className="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-slate-900/50 rounded-lg border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-white"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <label className="block text-sm font-medium text-slate-300">
                                    Weekly Fee
                                  </label>
                                  <input
                                    type="number"
                                    value={house.fee}
                                    onChange={(e) =>
                                      handleHouseChange(
                                        houseIndex,
                                        "fee",
                                        parseFloat(e.target.value) || 0
                                      )
                                    }
                                    className="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-slate-900/50 rounded-lg border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-white"
                                  />
                                </div>
                              </div>

                              <div className="space-y-3 sm:space-y-4">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 bg-slate-800/50 rounded-lg gap-2">
                                  <label className="text-sm font-medium text-slate-300">
                                    Number of Floors
                                  </label>
                                  <input
                                    type="number"
                                    min="1"
                                    value={house.floors.length}
                                    onChange={(e) =>
                                      handleFloorChange(
                                        houseIndex,
                                        parseInt(e.target.value) || 1
                                      )
                                    }
                                    className="w-full sm:w-20 px-3 py-2 bg-slate-900/50 rounded-lg border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-white text-center"
                                  />
                                </div>

                                {house.floors.map((floor, floorIndex) => (
                                  <div
                                    key={floorIndex}
                                    className="bg-slate-800/50 rounded-lg"
                                  >
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between px-3 sm:px-4 py-3 border-b border-slate-700/50 gap-2">
                                      <h4 className="text-white font-medium text-sm sm:text-base">
                                        Floor {floor.id}
                                      </h4>
                                      <div className="flex items-center space-x-2">
                                        <span className="text-xs sm:text-sm text-slate-400">
                                          Rooms:
                                        </span>
                                        <input
                                          type="number"
                                          min="1"
                                          value={floor.rooms.length}
                                          onChange={(e) =>
                                            handleRoomChange(
                                              houseIndex,
                                              floorIndex,
                                              parseInt(e.target.value) || 1
                                            )
                                          }
                                          className="w-20 px-3 py-2 bg-slate-900/50 rounded-lg border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-white text-center"
                                        />
                                      </div>
                                    </div>

                                    <div className="p-3 sm:p-4 space-y-2 sm:space-y-3">
                                      {floor.rooms.map((room, roomIndex) => (
                                        <div
                                          key={roomIndex}
                                          className="flex flex-col sm:flex-row sm:items-center gap-2 p-2 sm:p-3 bg-slate-900/20 rounded-lg hover:bg-slate-900/30 transition-colors"
                                        >
                                          <input
                                            type="text"
                                            value={room.name}
                                            onChange={(e) =>
                                              handleRoomNameChange(
                                                houseIndex,
                                                floorIndex,
                                                roomIndex,
                                                e.target.value
                                              )
                                            }
                                            placeholder="Room Name"
                                            className="flex-1 px-3 py-2 bg-slate-800/50 rounded-lg border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-white text-sm"
                                          />
                                          <div className="flex items-center justify-between sm:justify-start w-full sm:w-auto space-x-2 mt-2 sm:mt-0">
                                            <span className="text-xs sm:text-sm text-slate-400">
                                              Beds:
                                            </span>
                                            <input
                                              type="number"
                                              min="1"
                                              value={room.beds.length}
                                              onChange={(e) =>
                                                handleBedChange(
                                                  houseIndex,
                                                  floorIndex,
                                                  roomIndex,
                                                  parseInt(e.target.value) || 1
                                                )
                                              }
                                              className="w-20 px-3 py-2 bg-slate-800/50 rounded-lg border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-white text-center text-sm"
                                            />
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Add New House Form */}
                    {isAddingHouse && (
                      <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-3 sm:p-6 space-y-4 sm:space-y-6">
                        <div className="flex items-center justify-between">
                          <h3 className="text-base sm:text-lg font-medium text-white flex items-center gap-2">
                            <Plus size={18} className="text-blue-500" />
                            Add New House
                          </h3>
                          <button
                            onClick={() => setIsAddingHouse(false)}
                            className="text-slate-400 hover:text-slate-300 p-1 hover:bg-slate-700/30 rounded-full transition-colors"
                          >
                            <X size={20} />
                          </button>
                        </div>

                        {/* House Name and Fee */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-slate-300">
                              House Name
                            </label>
                            <input
                              type="text"
                              value={newHouse.name}
                              onChange={(e) =>
                                setNewHouse({
                                  ...newHouse,
                                  name: e.target.value,
                                })
                              }
                              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-slate-900/50 rounded-lg border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-white"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-slate-300">
                              Weekly Fee
                            </label>
                            <div className="relative">
                              <span className="absolute left-3 top-2.5 text-slate-500">
                                $
                              </span>
                              <input
                                type="number"
                                value={newHouse.fee}
                                onChange={(e) =>
                                  setNewHouse({
                                    ...newHouse,
                                    fee: parseFloat(e.target.value) || 0,
                                  })
                                }
                                className="w-full px-4 py-2 sm:py-2.5 pl-7 bg-slate-900/50 rounded-lg border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-white"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Number of Floors */}
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-slate-300">
                            Number of Floors
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={newHouse.floors.length}
                            onChange={(e) => {
                              const numberOfFloors =
                                parseInt(e.target.value) || 1;
                              setNewHouse({
                                ...newHouse,
                                floors: Array(numberOfFloors)
                                  .fill(null)
                                  .map((_, floorIndex) => ({
                                    id: floorIndex + 1,
                                    rooms: newHouse.floors[floorIndex]
                                      ?.rooms || [
                                      {
                                        id: 1,
                                        name: "Room 1",
                                        beds: [{ id: "1A", resident: null }],
                                      },
                                    ],
                                  })),
                              });
                            }}
                            className="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-slate-900/50 rounded-lg border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-white"
                          />
                        </div>

                        {/* Floors, Rooms, and Beds */}
                        {newHouse.floors.map((floor, floorIndex) => (
                          <div
                            key={floorIndex}
                            className="bg-slate-800/30 p-3 sm:p-4 rounded-lg space-y-3 sm:space-y-4"
                          >
                            <h4 className="text-white font-medium text-sm sm:text-base flex items-center gap-2">
                              <Home size={16} className="text-blue-400" />
                              Floor {floor.id}
                            </h4>
                            <div className="space-y-2">
                              <label className="block text-xs sm:text-sm font-medium text-slate-300">
                                Number of Rooms
                              </label>
                              <input
                                type="number"
                                min="1"
                                value={floor.rooms.length}
                                onChange={(e) => {
                                  const numberOfRooms =
                                    parseInt(e.target.value) || 1;
                                  const updatedRooms = Array(numberOfRooms)
                                    .fill(null)
                                    .map((_, roomIndex) => ({
                                      id: roomIndex + 1,
                                      name:
                                        floor.rooms[roomIndex]?.name ||
                                        `Room ${roomIndex + 1}`,
                                      beds: floor.rooms[roomIndex]?.beds || [
                                        {
                                          id: `${floor.id}${String.fromCharCode(
                                            65 + roomIndex
                                          )}1`,
                                          resident: null,
                                        },
                                      ],
                                    }));
                                  const updatedFloors = [...newHouse.floors];
                                  updatedFloors[floorIndex] = {
                                    ...floor,
                                    rooms: updatedRooms,
                                  };
                                  setNewHouse({
                                    ...newHouse,
                                    floors: updatedFloors,
                                  });
                                }}
                                className="w-full px-3 sm:px-4 py-2 bg-slate-900/50 rounded-lg border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-white"
                              />
                            </div>

                            {floor.rooms.map((room, roomIndex) => (
                              <div
                                key={roomIndex}
                                className="space-y-2 bg-slate-900/10 p-3 rounded-lg"
                              >
                                <label className="block text-xs sm:text-sm font-medium text-slate-300">
                                  Room {room.id}
                                </label>
                                <input
                                  type="text"
                                  value={room.name}
                                  onChange={(e) => {
                                    const updatedRooms = [...floor.rooms];
                                    updatedRooms[roomIndex] = {
                                      ...room,
                                      name: e.target.value,
                                    };
                                    const updatedFloors = [...newHouse.floors];
                                    updatedFloors[floorIndex] = {
                                      ...floor,
                                      rooms: updatedRooms,
                                    };
                                    setNewHouse({
                                      ...newHouse,
                                      floors: updatedFloors,
                                    });
                                  }}
                                  className="w-full px-3 sm:px-4 py-2 bg-slate-900/50 rounded-lg border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-white text-sm"
                                />
                                <div className="space-y-2">
                                  <label className="block text-xs sm:text-sm font-medium text-slate-300">
                                    Number of Beds
                                  </label>
                                  <input
                                    type="number"
                                    min="1"
                                    value={room.beds.length}
                                    onChange={(e) => {
                                      const numberOfBeds =
                                        parseInt(e.target.value) || 1;
                                      const updatedBeds = Array(numberOfBeds)
                                        .fill(null)
                                        .map((_, bedIndex) => ({
                                          id: `${floor.id}${String.fromCharCode(
                                            65 + roomIndex
                                          )}${bedIndex + 1}`,
                                          resident: null,
                                        }));
                                      const updatedRooms = [...floor.rooms];
                                      updatedRooms[roomIndex] = {
                                        ...room,
                                        beds: updatedBeds,
                                      };
                                      const updatedFloors = [
                                        ...newHouse.floors,
                                      ];
                                      updatedFloors[floorIndex] = {
                                        ...floor,
                                        rooms: updatedRooms,
                                      };
                                      setNewHouse({
                                        ...newHouse,
                                        floors: updatedFloors,
                                      });
                                    }}
                                    className="w-full px-3 sm:px-4 py-2 bg-slate-900/50 rounded-lg border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-white text-sm"
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        ))}

                        {/* Add Button */}
                        <button
                          onClick={() => {
                            // Validate inputs
                            if (!newHouse.name || newHouse.fee <= 0) {
                              setToast({
                                message:
                                  "Please provide a valid house name and fee.",
                                type: "error",
                              });
                              return;
                            }
                            const isDuplicateHouse = houses.some(
                              (house) => house.name === newHouse.name
                            );
                            if (isDuplicateHouse) {
                              setToast({
                                message: "House with this name already exists.",
                                type: "error",
                              });
                              return;
                            }
                            // Add house
                            setHouses([...houses, newHouse]);
                            setIsAddingHouse(false);
                            // Reset new house
                            setNewHouse({
                              name: "",
                              fee: 0,
                              color: "",
                              floors: [
                                {
                                  id: 1,
                                  rooms: [
                                    {
                                      id: 1,
                                      name: "Room 1",
                                      beds: [{ id: "1A", resident: null }],
                                    },
                                  ],
                                },
                              ],
                            });
                            setToast({
                              message: "House added successfully!",
                              type: "success",
                            });
                          }}
                          className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                          <Plus size={18} />
                          Add House
                        </button>
                      </div>
                    )}

                    <button
                      onClick={handleSaveHouseChanges}
                      className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 sticky bottom-0"
                    >
                      <Save size={18} />
                      Save House Changes
                    </button>
                  </div>
                )}

              {/* Shadow gradient at the bottom for scrolling cue */}
              <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-slate-900 to-transparent opacity-70 pointer-events-none z-10"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
